import { useState, useEffect, useSyncExternalStore } from 'react';

// ─── Breakpoints (matches existing codebase conventions) ────────────────
const DESKTOP_BP = 600;
const LARGE_DESKTOP_BP = 1024;

// ─── Module-level resize tracking (shared across all hook instances) ────
let _width = typeof window !== 'undefined' ? window.innerWidth : 1024;
let _height = typeof window !== 'undefined' ? window.innerHeight : 768;
const _listeners = new Set<() => void>();
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

function _handleResize() {
  if (_debounceTimer) clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w !== _width || h !== _height) {
      _width = w;
      _height = h;
      _listeners.forEach(fn => fn());
    }
  }, 150);
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', _handleResize);
}

// ─── External store API (for useSyncExternalStore) ──────────────────────
function subscribe(callback: () => void) {
  _listeners.add(callback);
  return () => { _listeners.delete(callback); };
}

function getSnapshot() {
  return `${_width}x${_height}`;
}

function getServerSnapshot() {
  return '1024x768';
}

// ─── Public hook ────────────────────────────────────────────────────────

export interface WindowSize {
  width: number;
  height: number;
  isDesktop: boolean;
  isLargeDesktop: boolean;
}

export default function useWindowSize(): WindowSize {
  // useSyncExternalStore ensures tear-free reads during concurrent rendering
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Derive values from the snapshot string (avoids stale closure issues)
  const [w, h] = snapshot.split('x').map(Number);

  return {
    width: w,
    height: h,
    isDesktop: w > DESKTOP_BP,
    isLargeDesktop: w > LARGE_DESKTOP_BP,
  };
}
